import { INSTRUCTOR_LINK_TAG_KEYS } from "@services/constants/instructorLinkTags";
import { InstructorLinkTagDefinition } from "@services/types/settings/settingsTypes";
import { InstructorLinkTags, InstructorLinkTagValue } from "./types";

export { INSTRUCTOR_LINK_TAG_KEYS };

/**
 * Fixed display order (per the approved design), independent of the order the
 * tags arrive in the setting array. Keys not listed here keep their setting
 * order and are appended after the known ones.
 */
export const INSTRUCTOR_LINK_TAG_DISPLAY_ORDER: string[] = [
  INSTRUCTOR_LINK_TAG_KEYS.assignmentDate,
  INSTRUCTOR_LINK_TAG_KEYS.dropDate,
  INSTRUCTOR_LINK_TAG_KEYS.paymentStartDate,
  INSTRUCTOR_LINK_TAG_KEYS.paymentEndDate,
  INSTRUCTOR_LINK_TAG_KEYS.paymentEnabled,
];

/**
 * Orders the setting's tag definitions by the fixed display order. Unknown keys
 * are appended preserving their original setting order.
 */
export const orderTagDefinitions = (
  tags: InstructorLinkTagDefinition[] = [],
): InstructorLinkTagDefinition[] => {
  const known = INSTRUCTOR_LINK_TAG_DISPLAY_ORDER.map(key =>
    tags.find(tag => tag?.key === key),
  ).filter((tag): tag is InstructorLinkTagDefinition => Boolean(tag));

  const unknown = tags.filter(tag => !INSTRUCTOR_LINK_TAG_DISPLAY_ORDER.includes(tag?.key));

  return [...known, ...unknown];
};

/**
 * Parses the raw `any` `instructorResponsability.tags` value into the typed
 * {@link InstructorLinkTags} map. A missing or malformed value resolves to an
 * empty map; an unexpected value type for a key resolves to `null`.
 */
export const parseInstructorLinkTags = (raw: unknown): InstructorLinkTags => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};

  return Object.entries(raw as Record<string, unknown>).reduce((acc, [key, value]) => {
    acc[key] =
      typeof value === "string" || typeof value === "boolean" || value === null
        ? (value as InstructorLinkTagValue)
        : null;

    return acc;
  }, {} as InstructorLinkTags);
};

/**
 * Converts a `YYYY-MM-DD` tag value (or the date part of a `YYYY-MM-DDThh:mm:ss`
 * term boundary) into a local `Date`, or `null` when unset/invalid.
 */
export const isoToDate = (iso?: InstructorLinkTagValue): Date | null => {
  if (typeof iso !== "string" || iso.trim() === "") return null;

  const [year, month, day] = iso.trim().slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);

  return Number.isNaN(date.getTime()) ? null : date;
};

/** Serializes a `Date` back to the `YYYY-MM-DD` format expected by the mutation. */
export const dateToIso = (date?: Date | null): string | null => {
  if (!date || Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/** Formats a `YYYY-MM-DD` tag value for read mode as `dd/mm/yy`, or `-` when unset. */
export const isoToDisplay = (iso?: InstructorLinkTagValue): string => {
  const date = isoToDate(iso);
  if (!date) return "-";

  const day = `${date.getDate()}`.padStart(2, "0");
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const year = `${date.getFullYear()}`.slice(-2);

  return `${day}/${month}/${year}`;
};
